import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useCategories } from "@/hooks/use-categories";
import { CheckCircle, Wrench, ChevronRight, ChevronLeft } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function BecomeTradesperson() {
  const { user, loginWithToken } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const categories = useCategories("job");
  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const redirectTo = params.get("redirect") ?? "/jobs";

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] bg-slate-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-5">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Wrench size={28} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Join as a Tradesperson</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Sign up or log in first, then set up your trade profile to start receiving paid job leads.
            </p>
          </div>
          <Link href="/signup?role=tradesperson">
            <button className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors">
              Create Trade Account
            </button>
          </Link>
          <Link href="/login">
            <button className="w-full border border-slate-200 text-slate-600 py-3.5 rounded-2xl text-sm hover:bg-slate-50 transition-colors">
              Already have an account? Sign in
            </button>
          </Link>
        </div>
      </div>
    );
  }

  if (user.role === "tradesperson" || user.role === "admin" || user.role === "business") {
    return (
      <div className="flex items-center justify-center min-h-[70vh] bg-slate-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle size={32} className="text-emerald-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">You already have trade access!</h2>
            <p className="text-sm text-slate-500 mt-2">Your account can browse and unlock job leads. Head to the Jobs page to get started.</p>
          </div>
          <button
            onClick={() => setLocation("/jobs")}
            className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors"
          >
            Browse Quote Requests
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] bg-slate-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-sm p-7 text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle size={32} className="text-emerald-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">You're a Tradesperson!</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Your trade account is now active. Browse quote requests and pay to unlock the contact details of residents who need your services.
            </p>
          </div>
          <button
            onClick={() => setLocation(redirectTo)}
            className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            Browse Quote Requests <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const toggle = (cat: string) =>
    setSelected((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]);

  const handleSubmit = async () => {
    if (selected.length === 0) {
      toast({ title: "Select at least one trade", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/become-tradesperson", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ categories: selected }),
      });
      const json = await res.json() as { token?: string; error?: string };
      if (!res.ok) throw new Error(json.error ?? "Could not register as tradesperson");
      if (json.token) await loginWithToken(json.token);
      setDone(true);
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-primary px-4 py-6">
        <div className="max-w-xl mx-auto">
          <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-white/80 hover:text-white mb-3 transition-colors">
            <ChevronLeft size={18} /> Back
          </button>
        </div>
        <div className="max-w-xl mx-auto flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Wrench size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-white font-bold text-xl">Join as a Tradesperson</h1>
            <p className="text-white/80 text-sm">Find paid job leads from local residents</p>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3">
          <h2 className="font-bold text-slate-800">How it works</h2>
          {[
            { emoji: "📋", text: "Residents post quote requests for jobs around their home" },
            { emoji: "🔓", text: "You pay a small fee to unlock the contact details" },
            { emoji: "📞", text: "Contact them directly and win the job" },
          ].map(({ emoji, text }) => (
            <div key={text} className="flex items-start gap-3">
              <span className="text-xl flex-shrink-0">{emoji}</span>
              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
          <div>
            <h2 className="font-bold text-slate-800">What trades do you offer?</h2>
            <p className="text-sm text-slate-500 mt-1">Select all that apply — you'll only see leads relevant to your trades.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = selected.includes(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggle(cat)}
                  className="px-3.5 py-1.5 rounded-full border text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: active ? "#1e3a5f" : "var(--color-card)",
                    borderColor: active ? "#1e3a5f" : "#e2e8f0",
                    color: active ? "var(--color-card)" : "#64748b",
                  }}
                >
                  {active ? "✓ " : ""}{cat}
                </button>
              );
            })}
          </div>
          {selected.length > 0 && (
            <p className="text-xs text-primary font-semibold">{selected.length} trade{selected.length !== 1 ? "s" : ""} selected</p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || selected.length === 0}
          className="w-full flex items-center justify-center gap-2.5 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-opacity disabled:opacity-60"
        >
          {submitting ? (
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Wrench size={17} />
              Activate Trade Account
            </>
          )}
        </button>

        <p className="text-xs text-slate-400 text-center leading-relaxed">
          You can update your trade profile and service areas any time from the{" "}
          <Link href="/business" className="text-primary hover:underline">Business Portal</Link>.
        </p>
      </div>
    </div>
  );
}
